import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import './LocationSettings.css';
import { StatusBadge } from '../../../shared/components/common/Badges';
import { formatPhoneNumber, removeCountryCode, validateIndianPhoneNumber } from '../../../utils/phoneFormatter';
import { formatCurrency, formatCurrencyDynamic, formatCurrencyInput, getCurrencyNumericValue } from '../../../utils/numberUtils';
import { toBoolean } from '../../../utils/booleanUtils';

const formatDateTime = (dateString) => {
  if (!dateString) return { date: '—', time: '' };
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return { date: '—', time: '' };
  return {
    date: date.toLocaleDateString(),
    time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  };
};

function StackedDateTime({ dateString }) {
  const { date, time } = formatDateTime(dateString);
  if (date === '—') {
    return <span className="info-value">—</span>;
  }
  return (
    <div
      className="info-value-stacked"
      title={new Date(dateString).toLocaleString()}
    >
      <div className="stack-item-date">{date}</div>
      {time && <div className="stack-item-time">{time}</div>}
    </div>
  );
}

function LocationSettings({ token, locationId, isCard = false, isEditingProp = false, onEditCancel = null, currentUser = null }) {
  const [location, setLocation] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    annual_budget: 0,
    high_cost_approval_threshold: ''
  });

  const formatAmountForDisplay = (value) => {
    if (!value || value === 0) return '';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;
    return numValue.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };

  // Determine if we're in controlled mode (parent controls editing)
  const isControlledMode = onEditCancel !== null;

  // Use parent prop if controlled, otherwise use local state
  const actualIsEditing = isControlledMode ? isEditingProp : isEditing;

  useEffect(() => {
    fetchLocation();
  }, [locationId]);

  const fetchLocation = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.LOCATIONS.GET(locationId), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setLocation(response.data);

      setFormData({
        name: response.data.name,
        contact_email: response.data.contact_email,
        contact_phone: response.data.contact_phone ? removeCountryCode(response.data.contact_phone) : '',
        address: response.data.address || '',
        city: response.data.city || '',
        state: response.data.state || '',
        country: response.data.country || '',
        annual_budget: formatAmountForDisplay(response.data.annual_budget),
        high_cost_approval_threshold: formatAmountForDisplay(response.data.high_cost_approval_threshold)
      });
    } catch (error) {
      console.error('Error fetching location:', error);
      toast.error('Failed to fetch location details');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // For contact_phone, keep only digits and limit to 10
    // Also prevent starting with 0 (invalid for Indian phone numbers)
    let finalValue = value;
    if (name === 'contact_phone') {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      // Prevent number starting with 0 - if user tries to enter 0, don't add it
      if (digits.length === 1 && digits === '0') {
        finalValue = '';
      } else if (digits.length > 1 && digits.startsWith('0')) {
        // If user pastes a number starting with 0, reject the entire input
        finalValue = '';
      } else {
        finalValue = digits;
      }
    } else if (name === 'annual_budget') {
      if (currentUser?.role !== 'superadmin') {
        return;
      }
      finalValue = formatCurrencyInput(value);
    } else if (name === 'high_cost_approval_threshold') {
      if (currentUser?.role !== 'admin') {
        return;
      }
      finalValue = formatCurrencyInput(value);
    }

    setFormData(prev => ({
      ...prev,
      [name]: finalValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.address?.trim() || !formData.city?.trim() || !formData.state?.trim() || !formData.country?.trim()) {
      toast.error('Address, city, state, and country are required');
      return;
    }

    // Validate contact_phone if provided
    if (formData.contact_phone) {
      const validation = validateIndianPhoneNumber(formData.contact_phone);
      if (!validation.isValid) {
        toast.error(`Invalid phone number: ${validation.error}`);
        return;
      }
    }

    try {
      // Prepare submission data.
      // IMPORTANT: Only superadmins are allowed to update annual_budget. For other roles,
      // omit the field entirely so the backend doesn't reject the request.
      const { annual_budget, high_cost_approval_threshold, ...restFormData } = formData;
      const submitData = {
        ...restFormData,
        ...(currentUser?.role === 'superadmin'
          ? { annual_budget: getCurrencyNumericValue(annual_budget) }
          : {}),
        ...(currentUser?.role === 'admin'
          ? { high_cost_approval_threshold: getCurrencyNumericValue(high_cost_approval_threshold) }
          : {})
      };

      // Step 1: Save to backend
      await axios.put(API_ENDPOINTS.LOCATIONS.UPDATE(locationId), submitData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Step 2: Refresh the location data
      try {
        await fetchLocation();
      } catch (fetchError) {
        console.error('[LocationSettings] Failed to refresh location:', fetchError);
      }

      toast.success('Location updated successfully!');

      // Step 3: Exit edit mode
      // In controlled mode, notify parent. In uncontrolled mode, manage locally.
      if (isControlledMode) {
        onEditCancel(); // Tell parent we're done editing
      } else {
        setIsEditing(false); // Local state management
      }
    } catch (error) {
      console.error('[LocationSettings] Error updating location:', error);
      toast.error(error.response?.data?.error || 'Failed to update location');
    }
  };

  const handleCancel = () => {
    // Reset form data
    if (location) {
      // Helper function to format numeric budget value for display
      setFormData({
        name: location.name,
        contact_email: location.contact_email,
        contact_phone: location.contact_phone || '',
        address: location.address || '',
        city: location.city || '',
        state: location.state || '',
        country: location.country || '',
        annual_budget: formatAmountForDisplay(location.annual_budget),
        high_cost_approval_threshold: formatAmountForDisplay(location.high_cost_approval_threshold)
      });
    }

    // Exit edit mode
    // In controlled mode, notify parent. In uncontrolled mode, manage locally.
    if (isControlledMode) {
      onEditCancel(); // Tell parent we're canceling
    } else {
      setIsEditing(false); // Local state management
    }
  };

  const handleCopyLocationCode = async () => {
    try {
      await navigator.clipboard.writeText(location.location_code);
      setIsCopied(true);
      // Reset the copied state after 2 seconds
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  if (!location) {
    return <div className="loading">Loading location details...</div>;
  }

  return (
    <div className="location-settings">
      {!isCard && (
        <div className="settings-header">
          <h2 className="page-title">Location Settings</h2>
          {!actualIsEditing && (
            <div className="action-palette">
              <button className="action-btn action-primary" onClick={() => setIsEditing(true)}>
                <span className="action-icon">✏️</span>
                <span className="action-text">Edit Settings</span>
              </button>
            </div>
          )}
        </div>
      )}

      <div className="settings-content">
        <div className="info-section">
          <h3>Location Details</h3>
          <p className="section-description">Manage your hospital location information and contact details</p>

          {!actualIsEditing ? (
            <div className="info-display">
              <div className="info-display-primary">
                <div className="info-item">
                  <span className="info-label">
                    <span className="label-icon">🏥</span>
                    Location Name
                  </span>
                  <span className="info-value">{location.name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">
                    <span className="label-icon">🔖</span>
                    Location Code
                  </span>
                  <button
                    type="button"
                    className={`location-code-badge ${isCopied ? 'copied' : ''}`}
                    onClick={handleCopyLocationCode}
                    title="Click to copy location code"
                  >
                    <span className="code-text">{location.location_code}</span>
                    <span className="copy-icon">
                      {isCopied ? '✓' : '📋'}
                    </span>
                  </button>
                </div>
                <div className="info-item">
                  <span className="info-label">Contact Email</span>
                  <span className="info-value">{location.contact_email}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Contact Phone</span>
                  <span className="info-value">
                    {location.contact_phone
                      ? formatPhoneNumber(location.contact_phone)
                      : '—'}
                  </span>
                </div>
              </div>
              {location.address && (
                <div className="info-item info-item--wide">
                  <span className="info-label">Address</span>
                  <span className="info-value">
                    {location.address}
                    {location.city && `, ${location.city}`}
                    {location.state && `, ${location.state}`}
                    {location.country && `, ${location.country}`}
                  </span>
                </div>
              )}
              <div className="info-item">
                <span className="info-label">Status</span>
                <StatusBadge status={toBoolean(location.is_active) ? 'active' : 'inactive'} size="small" />
              </div>
              <div className="info-item">
                <span className="info-label">Max Users</span>
                <span className="info-value">{location.max_users}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Max Devices</span>
                <span className="info-value">{location.max_devices}</span>
              </div>
              <div className="info-item">
                <span className="info-label">
                  <span className="label-icon">📋</span>
                  High-cost approval threshold
                </span>
                <span className="info-value">
                  {location.high_cost_approval_threshold && location.high_cost_approval_threshold > 0
                    ? `${formatCurrency(location.high_cost_approval_threshold)}`
                    : 'Not set'}
                  {location.high_cost_approval_threshold && location.high_cost_approval_threshold > 0 && (
                    <span style={{ fontWeight: 'bold', marginLeft: '0.5rem' }}>
                      ({formatCurrencyDynamic(location.high_cost_approval_threshold)})
                    </span>
                  )}
                </span>
                <small style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                  {currentUser?.role === 'admin'
                    ? 'Devices pending approval above this cost are flagged as high-cost. Edit to change.'
                    : 'Configured by the location administrator.'}
                </small>
              </div>
              <div className="info-item">
                <span className="info-label">
                  <span className="label-icon">💰</span>
                  Annual Budget
                </span>
                <span className="info-value">
                  {location.annual_budget && location.annual_budget > 0
                    ? `${formatCurrency(location.annual_budget)}`
                    : '₹0 (Not Set)'}
                  {location.annual_budget && location.annual_budget > 0 && (
                    <span style={{ fontWeight: 'bold', marginLeft: '0.5rem' }}>
                      ({formatCurrencyDynamic(location.annual_budget)})
                    </span>
                  )}
                </span>
                {currentUser?.role === 'superadmin' && (
                  <small style={{ display: 'block', marginTop: '0.25rem', color: '#666' }}>
                    Superadmin only - Edit to modify budget threshold
                  </small>
                )}
              </div>
              <div className="info-item">
                <span className="info-label">Created</span>
                <StackedDateTime dateString={location.created_at} />
              </div>
              <div className="info-item">
                <span className="info-label">Last Updated</span>
                <StackedDateTime dateString={location.updated_at} />
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="settings-form">
              <div className="form-group form-group--full">
                <label htmlFor="name">Location Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="contact_email">Contact Email *</label>
                <input
                  type="email"
                  id="contact_email"
                  name="contact_email"
                  value={formData.contact_email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group form-group--full">
                <label htmlFor="contact_phone">Contact Phone</label>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'stretch', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 auto', minWidth: '200px', display: 'flex', flexDirection: 'column' }}>
                    <input
                      type="tel"
                      id="contact_phone"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      maxLength="10"
                      placeholder="e.g., 9876543210"
                    />
                    <small className="hint" style={{ whiteSpace: 'normal', overflow: 'visible', lineHeight: '1.3', marginTop: '0.25rem' }}>For emergency contact & SMS notifications</small>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', justifyContent: 'flex-start', minWidth: '160px', flex: '1 1 200px' }}>
                    {(() => {
                      const digits = formData.contact_phone ? formData.contact_phone.replace(/\D/g, '') : '';
                      const validation = formData.contact_phone ? validateIndianPhoneNumber(formData.contact_phone) : { isValid: true, error: null };
                      const statusColor = formData.contact_phone && !validation.isValid ? '#d32f2f' : digits.length === 10 ? '#27ae60' : '#f59e0b';
                      const statusText = formData.contact_phone ? `${digits.length}/10` : '-';
                      const statusIcon = !formData.contact_phone ? '-' : validation.isValid ? '✓' : '✗';
                      return (
                        <>
                          <small style={{ color: statusColor, fontWeight: '600', fontSize: '0.85rem', textAlign: 'center', lineHeight: '1.4', marginTop: '0.5rem' }}>
                            {statusIcon} {statusText}
                          </small>
                          {formData.contact_phone && !validation.isValid && (
                            <small style={{ color: '#d32f2f', fontSize: '0.75rem', textAlign: 'center', lineHeight: '1.3', fontWeight: '500', whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'hidden' }}>
                              {validation.error}
                            </small>
                          )}
                          {formData.contact_phone && validation.isValid && (
                            <small style={{ color: '#666', fontSize: '0.8rem', whiteSpace: 'nowrap', lineHeight: '1.2', textAlign: 'center' }}>
                              {formatPhoneNumber(formData.contact_phone)}
                            </small>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              <div className="form-group form-group--full">
                <label htmlFor="address">Address *</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City *</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="state">State *</label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="country">Country *</label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* High-cost approval threshold — location admin only */}
              <div className="form-group form-group--full">
                <label htmlFor="high_cost_approval_threshold">
                  <span style={{ marginRight: '0.5rem' }}>📋</span>
                  High-cost approval threshold
                  {currentUser?.role === 'admin' ? (
                    <span style={{ color: '#6b7280', fontSize: '0.85rem' }}> (Location admin)</span>
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}> (Read-only)</span>
                  )}
                </label>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'stretch', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 auto', minWidth: '200px', display: 'flex', flexDirection: 'column' }}>
                    <input
                      type="text"
                      id="high_cost_approval_threshold"
                      name="high_cost_approval_threshold"
                      value={formData.high_cost_approval_threshold}
                      onChange={handleChange}
                      disabled={currentUser?.role !== 'admin'}
                      placeholder="e.g., 50,00,000"
                      title={currentUser?.role !== 'admin'
                        ? 'Only the location administrator can modify this threshold'
                        : 'Enter amount in INR. Devices pending approval above this value are flagged as high-cost.'}
                    />
                    <small className="hint" style={{ whiteSpace: 'normal', overflow: 'visible', lineHeight: '1.3', marginTop: '0.25rem' }}>
                      {currentUser?.role === 'admin'
                        ? 'Devices pending approval with cost above this amount are flagged as high-cost.'
                        : 'Configured by the location administrator.'}
                    </small>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', justifyContent: 'flex-start', minWidth: '160px', flex: '1 1 200px', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
                    {(() => {
                      const numericValue = getCurrencyNumericValue(formData.high_cost_approval_threshold);
                      const displayValue = numericValue && numericValue > 0 ? formatCurrency(numericValue) : '₹0';
                      const displayValueDyn = numericValue && numericValue > 0 ? formatCurrencyDynamic(numericValue) : '₹0';
                      return (
                        <>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <small style={{ color: '#6b7280', fontSize: '0.7rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Full Format
                            </small>
                            <div style={{ color: '#1f2937', fontWeight: '700', fontSize: '1rem', lineHeight: '1.4' }}>
                              {displayValue}
                            </div>
                          </div>
                          {numericValue && numericValue > 0 && (
                            <>
                              <div style={{ height: '1px', backgroundColor: '#e5e7eb' }} />
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <small style={{ color: '#6b7280', fontSize: '0.7rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  Compact Format
                                </small>
                                <div style={{ color: '#059669', fontWeight: '600', fontSize: '0.95rem', lineHeight: '1.4' }}>
                                  {displayValueDyn}
                                </div>
                              </div>
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Annual Budget Field - Superadmin Only */}
              <div className="form-group form-group--full">
                <label htmlFor="annual_budget">
                  <span style={{ marginRight: '0.5rem' }}>💰</span>
                  Annual Budget
                  {currentUser?.role === 'superadmin' ? (
                    <span style={{ color: '#6b7280', fontSize: '0.85rem' }}> (Superadmin)</span>
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}> (Read-only)</span>
                  )}
                </label>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'stretch', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 auto', minWidth: '200px', display: 'flex', flexDirection: 'column' }}>
                    <input
                      type="text"
                      id="annual_budget"
                      name="annual_budget"
                      value={formData.annual_budget}
                      onChange={handleChange}
                      disabled={currentUser?.role !== 'superadmin'}
                      placeholder="e.g., 10,00,000.00"
                      title={currentUser?.role !== 'superadmin' ? 'Only superadmins can modify the budget' : 'Enter budget amount. Formatting with thousands separators will be applied automatically.'}
                    />
                    <small className="hint" style={{ whiteSpace: 'normal', overflow: 'visible', lineHeight: '1.3', marginTop: '0.25rem' }}>
                      {currentUser?.role === 'superadmin'
                        ? 'Set the annual budget threshold for this location. Budget overrun alerts will be generated when spending exceeds this amount.'
                        : 'Current budget threshold for this location. Only superadmins can modify this value.'}
                    </small>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', justifyContent: 'flex-start', minWidth: '160px', flex: '1 1 200px', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }}>
                    {(() => {
                      const numericValue = getCurrencyNumericValue(formData.annual_budget);
                      const displayValue = numericValue && numericValue > 0 ? formatCurrency(numericValue) : '₹0';
                      const displayValueDyn = numericValue && numericValue > 0 ? formatCurrencyDynamic(numericValue) : '₹0';
                      const isZero = !numericValue || numericValue === '0' || numericValue === 0;

                      return (
                        <>
                          {/* Full Format Display */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <small style={{ color: '#6b7280', fontSize: '0.7rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                              Full Format
                            </small>
                            <div style={{ color: '#1f2937', fontWeight: '700', fontSize: '1rem', lineHeight: '1.4' }}>
                              {displayValue}
                            </div>
                          </div>

                          {/* Compact Format Display (if not zero) */}
                          {!isZero && (
                            <>
                              <div style={{ height: '1px', backgroundColor: '#e5e7eb' }} />
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                <small style={{ color: '#6b7280', fontSize: '0.7rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                  Compact Format
                                </small>
                                <div style={{ color: '#059669', fontWeight: '600', fontSize: '0.95rem', lineHeight: '1.4' }}>
                                  {displayValueDyn}
                                </div>
                              </div>
                            </>
                          )}

                          {/* Hint Text */}
                          <small style={{ color: '#9ca3af', fontSize: '0.7rem', lineHeight: '1.3', marginTop: '0.25rem', fontStyle: 'italic' }}>
                            Updates as you type
                          </small>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="action-btn action-secondary" onClick={handleCancel}>
                  <span className="action-icon">✕</span>
                  <span className="action-text">Cancel</span>
                </button>
                <button type="submit" className="action-btn action-primary">
                  <span className="action-icon">💾</span>
                  <span className="action-text">Save Changes</span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default LocationSettings;
